import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

const DB_NAME = 'expanding_minefield_db';

class DatabaseService {
  private sqlite: SQLiteConnection | null = null;
  private db: SQLiteDBConnection | null = null;
  private initPromise: Promise<SQLiteDBConnection | null> | null = null;
  public useWebFallback: boolean = false;

  public async initialize(): Promise<SQLiteDBConnection | null> {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      // Check if running on Native Android/iOS platform
      if (!Capacitor.isNativePlatform()) {
        // Web browser environment: use WebStorage persistence
        this.useWebFallback = true;
        return null;
      }

      try {
        this.sqlite = new SQLiteConnection(CapacitorSQLite);

        const ret = await this.sqlite.checkConnectionsConsistency();
        const isConn = (await this.sqlite.isConnection(DB_NAME, false)).result;

        if (ret.result && isConn) {
          this.db = await this.sqlite.retrieveConnection(DB_NAME, false);
        } else {
          this.db = await this.sqlite.createConnection(
            DB_NAME,
            false,
            'no-encryption',
            1,
            false
          );
        }

        await this.db.open();
        await this.runMigrations();
        return this.db;
      } catch (err) {
        console.warn('Native SQLite connection notice: using WebStorage fallback:', err);
        this.useWebFallback = true;
        return null;
      }
    })();

    return this.initPromise;
  }

  private async runMigrations(): Promise<void> {
    if (!this.db) return;

    const schema = `
      CREATE TABLE IF NOT EXISTS game (
        id INTEGER PRIMARY KEY DEFAULT 1,
        seed TEXT NOT NULL,
        reward_points INTEGER NOT NULL DEFAULT 0,
        failure_count INTEGER NOT NULL DEFAULT 0,
        camera_x REAL NOT NULL DEFAULT 0,
        camera_y REAL NOT NULL DEFAULT 0,
        zoom REAL NOT NULL DEFAULT 1
      );

      CREATE TABLE IF NOT EXISTS boxes (
        box_x INTEGER NOT NULL,
        box_y INTEGER NOT NULL,
        status TEXT NOT NULL,
        PRIMARY KEY (box_x, box_y)
      );

      CREATE TABLE IF NOT EXISTS cells (
        box_x INTEGER NOT NULL,
        box_y INTEGER NOT NULL,
        cell_x INTEGER NOT NULL,
        cell_y INTEGER NOT NULL,
        state TEXT NOT NULL,
        PRIMARY KEY (box_x, box_y, cell_x, cell_y)
      );

      CREATE TABLE IF NOT EXISTS settings (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL
      );
    `;

    await this.db.execute(schema);
  }

  public async getDb(): Promise<SQLiteDBConnection | null> {
    if (!this.db && !this.useWebFallback) {
      await this.initialize();
    }
    return this.db;
  }

  public async saveStore(): Promise<void> {
    // Native SQLite auto-commits transactions
  }
}

export const dbService = new DatabaseService();
